<script setup lang="ts">
import { reactive, ref, computed, watch, onMounted } from 'vue'
import UiCustomCheckbox from '@/components/UiCustomCheckbox/UiCustomCheckbox.vue'
import { useRoute } from 'vue-router'
import axios from 'axios'
import { vMaska } from 'maska'
import parsePhoneNumber, { type CountryCode } from 'libphonenumber-js'
import { countries, masks, codes } from '@/helpers/countries'
import type { Rule } from 'ant-design-vue/es/form'
import type { FormOptions } from '@/types'

interface Props {
  formOptions: FormOptions | null
  offerUrls: string[]
  // Показывать общую галку «Соглашаюсь со всеми условиями» (настройка партнёра).
  showAcceptAll?: boolean
  // Куда постить форму. По умолчанию — черновик-инвойс /d/{uuid}.
  submitUrl?: string
  // Поле ответа, по которому делать редирект.
  redirectField?: string
  // Если задан — вызывается с данными ответа вместо авторедиректа.
  // Позволяет обработать ответ снаружи (например, показать витрину оферов).
  onSubmitSuccess?: (data: any) => void
  // Предзаполнение полей (например, данными клиента из счёта).
  initialValues?: Partial<Pick<FormState, 'surname' | 'name' | 'patronymic' | 'phone' | 'country_code' | 'email'>>
}

interface FormState {
  surname: string
  name: string
  patronymic: string
  phone: string
  country_code: string
  email: string
  accept_offer: boolean
  accept_offer_school: boolean
  accept_policy: boolean
  accept_personal: boolean
}

const props = defineProps<Props>()
const route = useRoute()

// -----------------
// Динамические ссылки
// -----------------
const domain = window.location.hostname
const ofertaPlatformLink = import.meta.env.VITE_AGREEMENT_URL
const ofertaSchoolLink = props.offerUrls[0]
const personalLink = import.meta.env.VITE_PERSONAL_DATA_URL
const policyLink = import.meta.env.VITE_POLICY_URL

// -----------------
// Стейт формы
// -----------------
const formState = reactive<FormState>({
  surname: '',
  name: '',
  patronymic: '',
  phone: '',
  country_code: 'RU',
  email: '',
  accept_offer: false,
  accept_offer_school: false,
  accept_policy: false,
  accept_personal: false
})

const serverErrors = reactive<Record<string, string>>({
  surname: '',
  name: '',
  patronymic: '',
  phone: '',
  email: ''
})

const options = ref<{ label: string; value: string }[]>()

onMounted(() => {
  options.value = Object.keys(countries).map((key: string) => ({
    value: key,
    label: `${countries[key]} ${codes[key]}`
  }))

  if (props.initialValues) {
    Object.assign(formState, props.initialValues)
  }
})

// -----------------
// Маска телефона
// -----------------
const mask = computed(() => masks[formState.country_code])

const validatePhone = (_rule: Rule, value: string) => {
  const phoneNumber = parsePhoneNumber(value, formState.country_code as CountryCode)
  if (phoneNumber?.isValid()) return Promise.resolve()
  else return Promise.reject(new Error('Пожалуйста укажите корректный номер телефона'))
}

// -----------------
// Валидация
// -----------------
const rules = computed<Record<string, Rule[]>>(() => ({
  surname: [{ required: !props.formOptions?.name_hidden, min: props.formOptions?.name_hidden ? 0 : 2, message: 'Пожалуйста заполните фамилию' }],
  name: [{ required: !props.formOptions?.name_hidden, min: props.formOptions?.name_hidden ? 0 : 2, message: 'Пожалуйста заполните имя' }],
  phone: [{ required: !props.formOptions?.phone_hidden, validator: validatePhone, trigger: 'change' }],
  email: [
    { required: true, message: 'Пожалуйста заполните email' },
    { type: 'email', message: 'Email не является действительным адресом' }
  ],
  accept_offer: [{ required: true, type: 'boolean', message: 'Подтвердите согласие с офертой TerPay' }],
  accept_offer_school: [{ required: true, type: 'boolean', message: 'Подтвердите согласие с офертой школы' }],
  accept_personal: [{ required: true, type: 'boolean', message: 'Подтвердите согласие с обработкой ПД' }],
  accept_policy: [{ required: true, type: 'boolean', message: 'Подтвердите согласие с политикой конфиденциальности' }]
}))

// -----------------
// "Принять всё" логика
// -----------------
const acceptAll = ref(false)

watch(acceptAll, (val) => {
  formState.accept_offer = val
  formState.accept_offer_school = val
  formState.accept_personal = val
  formState.accept_policy = val
})

watch(
  () => [formState.accept_offer, formState.accept_offer_school, formState.accept_personal, formState.accept_policy],
  () => {
    const checks = [formState.accept_offer, formState.accept_personal, formState.accept_policy]
    if (ofertaSchoolLink) checks.push(formState.accept_offer_school)
    acceptAll.value = checks.every(Boolean)
  }
)

const allAccepted = computed(() => {
  const checks = [formState.accept_offer, formState.accept_personal, formState.accept_policy]
  if (ofertaSchoolLink) checks.push(formState.accept_offer_school)
  return checks.every(Boolean)
})

// -----------------
// Submit
// -----------------
const isLoading = ref(false)

const onFinish = async (values: any) => {
  if (isLoading.value) return
  isLoading.value = true

  // Конкатенируем ФИО (только если поля не скрыты)
  if (!props.formOptions?.name_hidden) {
    values.name = [values.surname, values.name, values.patronymic].filter(Boolean).join(' ')
  }

  try {
    const url = props.submitUrl ?? `/d/${route.params.uuid}`
    const redirectField = props.redirectField ?? 'link'
    const response = await axios.post(url, values)

    if (props.onSubmitSuccess) {
      props.onSubmitSuccess(response.data)
      return
    }

    window.location.href = response.data[redirectField]
  } catch (err: any) {
    const errors = err?.response?.data?.errors
    if (errors) {
      Object.keys(errors).forEach((key) => {
        serverErrors[key] = errors[key][0] || ''
      })
    }
  } finally {
    isLoading.value = false
  }
}

const filterOption = (input: string, option: any) => {
  return option.label.toLowerCase().includes(input.toLowerCase())
}
</script>

<template>
  <div class="ui-form container">
<!--    <span v-if="formOptions?.form_title" class="ui-form__title">-->
<!--      {{ formOptions.form_title }}-->
<!--    </span>-->

<!--    <span v-else class="ui-form__title">-->
<!--      Заполните информацию о вас <br />для совершения оплаты-->
<!--    </span>-->

    <h2>
      Введите свои данные для совершения оплаты
    </h2>

    <a-form
      class="ui-form-wrapper"
      :model="formState"
      :requiredMark="false"
      layout="vertical"
      :rules="rules"
      @finish="onFinish"
    >
      <a-form-item
        class="ui-input"
        v-if="!formOptions?.name_hidden"
        name="surname"
        :validateStatus="serverErrors['surname'] ? 'error' : undefined"
        :help="serverErrors['surname'] || undefined"
      >
        <template #label>
          <div class="ui-input__label">
            Фамилия <span class="ui-input__hint">(как в паспорте)</span>
          </div>
        </template>

        <a-input class="ui-input__field" v-model:value="formState.surname" size="large" placeholder="Иванов" />
      </a-form-item>

      <a-form-item
        class="ui-input"
        v-if="!formOptions?.name_hidden"
        name="name"
        :validateStatus="serverErrors['name'] ? 'error' : undefined"
        :help="serverErrors['name'] || undefined"
      >
        <template #label>
          <div class="ui-input__label">
            Имя <span class="ui-input__hint">(как в паспорте)</span>
          </div>
        </template>

        <a-input class="ui-input__field" v-model:value="formState.name" size="large" placeholder="Сергей" />
      </a-form-item>

      <a-form-item
        class="ui-input"
        v-if="!formOptions?.name_hidden"
        name="patronymic"
        :validateStatus="serverErrors['patronymic'] ? 'error' : undefined"
        :help="serverErrors['patronymic'] || undefined"
      >
        <template #label>
          <div class="ui-input__label">
            Отчество <span class="ui-input__hint">(как в паспорте)</span>
          </div>
        </template>

        <a-input class="ui-input__field" v-model:value="formState.patronymic" size="large" placeholder="Петрович" />
      </a-form-item>

      <a-form-item
        v-if="!formOptions?.phone_hidden"
        class="ui-input"
        name="phone"
        :validateStatus="serverErrors['phone'] ? 'error' : undefined"
        :help="serverErrors['phone'] || undefined"
      >
        <template #label>
          <div class="ui-input__label">
            Телефон
          </div>
        </template>

        <a-input-group class="ui-input__field-wrapper" compact label="Телефон">
          <a-form-item-rest>
            <a-select
              style="flex: 1;"
              v-model:value="formState.country_code"
              size="large"
              notFoundContent="Не найдено"
              :options="options"
              :filter-option="filterOption"
            />
          </a-form-item-rest>

          <a-input
            class="ui-input__custom-field"
            name="phone"
            v-maska
            :data-maska="mask"
            :placeholder="mask.replace(new RegExp(/#/g), '0')"
            v-model:value="formState.phone"
            size="large"
          />
        </a-input-group>
      </a-form-item>

      <a-form-item
        class="ui-input"
        name="email"
        :validateStatus="serverErrors['email'] ? 'error' : undefined"
        :help="serverErrors['email'] || undefined"
      >
        <template #label>
          <div class="ui-input__label">
            Email
          </div>
        </template>

        <a-input class="ui-input__field" v-model:value="formState.email" size="large" placeholder="example@domain.com" />
      </a-form-item>

      <!--    Checkboxes   -->
      <a-form-item v-if="props.showAcceptAll !== false">
        <ui-custom-checkbox v-model="acceptAll" label>
          <strong>Ознакомился и соглашаюсь со всеми условиями</strong>
        </ui-custom-checkbox>
      </a-form-item>

      <a-form-item
        v-if="ofertaSchoolLink"
        name="accept_offer_school"
        :rules="[{ required: true, type: 'boolean', message: 'Подтвердите согласие' }]"
      >
        <ui-custom-checkbox
          v-model="formState.accept_offer_school"
          label
          required
        >
          Ознакомился и соглашаюсь с условиями заключения договора
          <a target="_blank" :href="ofertaSchoolLink">(оферта школы)</a>
        </ui-custom-checkbox>
      </a-form-item>

      <a-form-item
        name="accept_offer"
        :rules="[{ required: true, type: 'boolean', message: 'Подтвердите согласие' }]"
      >
        <ui-custom-checkbox
          v-model="formState.accept_offer"
          label
          required
        >
          Ознакомился и соглашаюсь с условиями
          <a target="_blank" :href="ofertaPlatformLink">пользовательского соглашения</a>
        </ui-custom-checkbox>
      </a-form-item>

      <a-form-item
        name="accept_personal"
        :rules="[{ required: true, type: 'boolean', message: 'Подтвердите согласие' }]"
      >
        <ui-custom-checkbox
          v-model="formState.accept_personal"
          label
          required
        >
          Ознакомился и соглашаюсь с <a target="_blank" :href="personalLink">обработкой своих персональных данных</a>
        </ui-custom-checkbox>
      </a-form-item>

      <a-form-item
        name="accept_policy"
        :rules="[{ required: true, type: 'boolean', message: 'Подтвердите согласие' }]"
      >
        <ui-custom-checkbox
          v-model="formState.accept_policy"
          label
          required
        >
          Ознакомился и соглашаюсь с <a target="_blank" :href="policyLink">политикой конфиденциальности</a>
        </ui-custom-checkbox>
      </a-form-item>



      <!-- <a-form-item name="accept_marketing" class="ui-form-group__item">
        <a-checkbox class="ui-form-group__checkbox" v-model:checked="formState.accept_marketing" />

        Даю
        <a
          class="ui-form-group__link"
          href="https://terpay.ru/static/agreement-marketing.pdf"
          target="_blank"
          >Согласие на получение рекламных материалов</a
        >
      </a-form-item> -->

      <a-form-item>
        <button class="ui-button" type="submit" :disabled="!allAccepted">
          {{ formOptions?.payment_button_name ? formOptions.payment_button_name : 'Выбрать способ оплаты' }}
        </button>
      </a-form-item>
    </a-form>
  </div>
</template>

<style lang="scss" scoped>
.ui-input {
  display: flex;
  row-gap: 10px;
  flex-direction: column;

  &__label {
    color: var(--color-black);
    font-size: 16px;
    line-height: 1.5;
    font-family: var(--font-family-alt);
  }

  &__hint {
    color: var(--color-gray-50);
  }

  &__field {
    transition-duration: var(--transition-duration);
    outline: 1px solid transparent;
    border: none;
    border-radius: var(--rounded-lg);
    background-color: var(--color-gray-10);
    padding: 4px 4px 4px 12px;
    height: 44px;
    font-size: 15px;

    &::placeholder {
      opacity: 0.3;
      color: var(--color-black);
    }

    &:focus {
      outline-color: var(--color-gray-10);
      background-color: transparent;
    }
  }

  &__field-wrapper {
    display: flex;
  }

  &__custom-field {
    border-radius: 0 var(--rounded-lg) var(--rounded-lg) 0 !important;
    background-color: var(--color-gray-10);
    padding: 12px 12px 12px 12px;
    width: 100%;
    height: 44px;
    font-size: 15px;
    line-height: 1.33;
    border: none;

    &::placeholder {
      color: var(--color-black-90);
    }
  }

  &:deep(.ant-select) {
    border-radius: var(--rounded-lg) 0 0 var(--rounded-lg);
    height: 44px;
    font-weight: 500;
    font-size: 15px;
    line-height: 1.33;
  }



  &:deep(.ant-select-selector) {
    background: var(--color-gray-10) !important;
    border-radius: var(--rounded-lg) 0 0 var(--rounded-lg) !important;
    padding: 0 12px !important;
    height: 44px !important;
    font-size: 15px;
    border: none !important;

    display: flex;
    align-items: center;
  }
}

.ui-button {
  border: 1px solid transparent;
  border-radius: var(--rounded-lg);
  background-color: var(--color-accent);
  padding-block: 11px;
  color: var(--color-black-99);
  font-size: 15px;
  line-height: 1.33;
  width: 100%;

  @include flex-center;

  @include hover {
    opacity: 0.7;
    transition-duration: var(--transition-duration);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
}

a {
  text-decoration: underline;
}
</style>
